const MIN_OVERLAP_MS = 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;

/** Minimum calendar block length in week/day time grid (react-big-calendar sizes by duration). */
export const JOB_EVENT_MIN_DURATION_MINUTES = 105;
export const VISIT_EVENT_MIN_DURATION_MINUTES = 120;

const pad = (value) => String(value).padStart(2, '0');

export const getDayKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const parseEventDate = (value) => {
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Ensures events span at least minMinutes so week/day views allocate enough height.
 * Returns whether the original range had no duration (start >= end).
 */
export const ensureMinimumEventDuration = (start, end, minMinutes) => {
  const startDate = parseEventDate(start);
  const endDate = parseEventDate(end);
  if (!startDate) {
    return { start: startDate, end: endDate, isPointInTime: false };
  }

  const resolvedEnd = endDate ?? new Date(startDate.getTime());
  const isPointInTime = resolvedEnd.getTime() <= startDate.getTime();
  const minEnd = new Date(startDate.getTime() + minMinutes * MS_PER_MINUTE);
  const displayEnd = resolvedEnd.getTime() < minEnd.getTime() ? minEnd : resolvedEnd;

  return {
    start: startDate,
    end: displayEnd,
    isPointInTime,
  };
};

export const hasExplicitTime = (event) => {
  if (!event) return false;

  if (event.resource?.technicianVisit?.scheduled_time) {
    return true;
  }

  const start = parseEventDate(event.start);
  const end = parseEventDate(event.end);
  if (!start || !end) return false;

  const startHasTime = start.getHours() !== 0 || start.getMinutes() !== 0 || start.getSeconds() !== 0;
  const endHasTime = end.getHours() !== 0 || end.getMinutes() !== 0 || end.getSeconds() !== 0;

  return startHasTime || endHasTime;
};

export const normalizeEventRange = (event) => {
  const start = parseEventDate(event?.start);
  const end = parseEventDate(event?.end);
  if (!start || !end) return null;

  if (end.getTime() > start.getTime()) {
    return { start, end };
  }

  // Zero-length events are treated as one-minute blocks for overlap checks.
  return { start, end: new Date(start.getTime() + MIN_OVERLAP_MS) };
};

export const hasMinuteOverlap = (leftEvent, rightEvent) => {
  const left = normalizeEventRange(leftEvent);
  const right = normalizeEventRange(rightEvent);
  if (!left || !right) return false;

  const overlapMs =
    Math.min(left.end.getTime(), right.end.getTime()) -
    Math.max(left.start.getTime(), right.start.getTime());

  return overlapMs >= MIN_OVERLAP_MS;
};

const buildOverlapClusters = (timedEvents) => {
  if (!timedEvents.length) return [];

  const parent = timedEvents.map((_, index) => index);

  const find = (index) => {
    if (parent[index] !== index) {
      parent[index] = find(parent[index]);
    }
    return parent[index];
  };

  const union = (leftIndex, rightIndex) => {
    const leftRoot = find(leftIndex);
    const rightRoot = find(rightIndex);
    if (leftRoot !== rightRoot) {
      parent[rightRoot] = leftRoot;
    }
  };

  for (let i = 0; i < timedEvents.length; i += 1) {
    for (let j = i + 1; j < timedEvents.length; j += 1) {
      if (hasMinuteOverlap(timedEvents[i], timedEvents[j])) {
        union(i, j);
      }
    }
  }

  const clustersByRoot = new Map();
  timedEvents.forEach((event, index) => {
    const root = find(index);
    if (!clustersByRoot.has(root)) {
      clustersByRoot.set(root, []);
    }
    clustersByRoot.get(root).push(event);
  });

  return [...clustersByRoot.values()].filter((cluster) => cluster.length > 1);
};

export const buildQueueMetadata = (events) => {
  const safeEvents = Array.isArray(events) ? events : [];

  const eventsByDay = new Map();
  safeEvents.forEach((event) => {
    const start = parseEventDate(event?.start);
    const dayKey = getDayKey(start);
    if (!dayKey) return;

    if (!eventsByDay.has(dayKey)) {
      eventsByDay.set(dayKey, []);
    }

    eventsByDay.get(dayKey).push(event);
  });

  const monthCounts = new Map();
  const overlapClusters = new Map();
  const eventClusterInfo = new Map();

  eventsByDay.forEach((dayEvents, dayKey) => {
    monthCounts.set(dayKey, dayEvents.length);

    const timedEvents = dayEvents.filter(hasExplicitTime);
    const clusters = buildOverlapClusters(timedEvents);

    clusters.forEach((clusterEvents, clusterIndex) => {
      const sortedCluster = [...clusterEvents].sort((a, b) => {
        const aStart = parseEventDate(a?.start)?.getTime() ?? 0;
        const bStart = parseEventDate(b?.start)?.getTime() ?? 0;
        return aStart - bStart;
      });

      const clusterId = `${dayKey}-${clusterIndex}`;
      const anchorEvent = sortedCluster[sortedCluster.length - 1];
      const moreCount = sortedCluster.length - 1;

      overlapClusters.set(clusterId, {
        dayKey,
        events: sortedCluster,
        anchorEventId: anchorEvent.id,
      });

      sortedCluster.forEach((event) => {
        eventClusterInfo.set(event.id, {
          clusterId,
          isAnchor: event.id === anchorEvent.id,
          moreCount,
        });
      });
    });
  });

  return {
    monthCounts,
    eventsByDay,
    overlapClusters,
    eventClusterInfo,
  };
};
